import { EnumMagicStrings } from '@/helpers/enums/misc.enum';
import vuetify from '@/plugins/vuetify';
import axios from 'axios';
import type { i18n } from 'i18next';
import i18next from 'i18next';
import { defineStore } from 'pinia';
import { ref } from 'vue';
import { useOrganizationStore } from './organzation.pinia';
import { pinia } from '@/pinia';
export const useLocaleStore = defineStore('localeStore', () => {
  /**
  >>>>>>>>>>>>>>> state variables >>>>>>>>>>>>>>>
  */
  const locale = ref<string>(
    localStorage.getItem(EnumMagicStrings.localeLanguage) || 'en'
  );
  const isLTR = ref<boolean>(locale.value === 'en');

  const tootlips = ref<Record<string, string>>({});

  return {
    locale,
    isLTR,
    changeLocale,
    loadLocaleMessages,
  };
  type LocalizationData = {
    student: Record<string, string>;
    tooltips: Record<string, string>;
  };
  /**
  >>>>>>>>>>>>>>> functions >>>>>>>>>>>>>>>
  */

  //>>>>>>> i18n shananigans >>>>>>>>>>>>>>>
  async function changeLocale(
    languageCode: string,
    i18next: i18n,
    setTerms: boolean = true
  ) {
    //>>>>>>> gets the messages from either cms or local storage >>>>>>>>>>>>>>>
    localStorage.setItem(EnumMagicStrings.localeLanguage, languageCode);
    locale.value = languageCode;
    isLTR.value = languageCode === 'en';
    // Keep the root element in sync for accessibility / SEO (KP-53): the
    // static index.html ships lang="en" with no dir, which is wrong the
    // moment the UI renders Arabic. changeLocale is the single locale
    // path — it runs on initial boot (plugins/i18n.ts) and on every
    // language toggle — so wiring it here covers both, idempotently.
    document.documentElement.lang = languageCode;
    document.documentElement.dir = isLTR.value ? 'ltr' : 'rtl';

    const messages = await loadLocaleMessages(languageCode);

    i18next.addResourceBundle(
      languageCode,
      'translation',
      messages[languageCode],
      true,
      true
    );

    await i18next.changeLanguage(languageCode);
    vuetify.locale.current.value = languageCode;
    if (setTerms) useOrganizationStore(pinia).setOrganizationTerms();
    return i18next;
  }

  /**
   * Every step below is pinned to the languageCode it was CALLED for instead
   * of reading the live `locale.value` when an async response settles (same
   * class as KP-30). The background CMS refresh used to key its save +
   * addResourceBundle off `locale.value` at RESPONSE time, so a language
   * toggle while a fetch was in flight persistently cross-poisoned the
   * localStorage cache and the i18next language slots: the app could then run
   * with lng='en' whose bundle carried Arabic CMS strings (the UI looks
   * Arabic) while seed-only keys — e.g. the KP-17/KP-62 lock messages —
   * resolved from the genuine `en` seeds and rendered English tooltips under
   * an Arabic UI.
   */
  async function loadLocaleMessages(languageCode: string = locale.value) {
    const messages: Record<string, Record<string, string>> = {};
    const localData = await getLocalizationsFromLocalStorage(languageCode);
    messages[languageCode] = localData.student;
    tootlips.value = localData.tooltips;
    return messages;
  }

  async function getDataFromCms(
    languageCode: string
  ): Promise<LocalizationData> {
    const headers = {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${import.meta.env.VITE_I18N_TOKEN}`,
    };
    const studentRes = await axios.get(
      `${import.meta.env.VITE_APP_CMS_BASE_URL}-${languageCode}`,
      {
        headers,
      }
    );

    const data: LocalizationData = {
      student: studentRes.data.data.attributes.json,
      tooltips: studentRes.data.data.attributes.json.tooltips,
    };
    saveDataToLocalStorage({ data, languageCode });
    return data;
  }

  function saveDataToLocalStorage({
    data,
    languageCode,
  }: {
    data: LocalizationData;
    languageCode: string;
  }) {
    localStorage.setItem(languageCode, JSON.stringify(data));

    i18next.addResourceBundle(
      languageCode,
      'translation',
      data.student,
      true,
      true
    );
  }

  async function getLocalizationsFromLocalStorage(languageCode: string) {
    const data = localStorage.getItem(languageCode);
    if (!data) return await getDataFromCms(languageCode);
    getDataFromCms(languageCode);
    return JSON.parse(data) as LocalizationData;
  }
});

//======================================================================
//>>>>>>>>>>>>>>> i18n shananigans >>>>>>>>>>>>>>>
//======================================================================
